import React, { useState, useEffect } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Layout } from "antd";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Sidebar from "./components/Layout/Sidebar";
import Header from "./components/Layout/Header";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Company from "./pages/Company";
import Checkpoints from "./pages/Checkpoints";
import PatrolRounds from "./pages/PatrolRounds";
import PatrolReports from "./pages/PatrolReports";
import Users from "./pages/Users";
import Reports from "./pages/Reports";
import AllCompanies from "./pages/SuperAdmin/AllCompanies";
// import SystemReports from "./pages/SuperAdmin/SystemReports";
import MultiCompanyAdmin from "./pages/MultiCompanyAdmin";
import GuardCompanyManagement from "./pages/SuperAdmin/GuardCompanyManagement";
import ActiveRounds from "./pages/SuperAdmin/ActiveRounds";
import GuardPerformance from "./pages/SuperAdmin/GuardPerformance";
import SilenceRequests from "./pages/SilenceRequests";
import ExecutiveDashboard from "./pages/ExecutiveDashboard";
import LoadingSpinner from "./components/UI/LoadingSpinner";

const { Content } = Layout;

// Route → required permission mapping (ตรงกับ Sidebar.js hasPermission)
const ROUTE_PERMISSIONS = {
  "/company": "manage_users",
  "/checkpoints": "manage_checkpoints",
  "/patrol-rounds": "manage_rounds",
  "/patrol-reports": "view_reports",
  "/users": "manage_users",
  "/reports": "view_reports",
};

function AppLayout() {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(window.innerWidth < 992);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 992;
      setIsMobile(mobile);
      if (mobile) {
        setCollapsed(true);
      } else {
        setCollapsed(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Redirect multi_company_admin to /dashboard if current route is not permitted
  useEffect(() => {
    if (user?.role === "multi_company_admin") {
      if (!user?.company_permissions) return;
      const requiredPermission = ROUTE_PERMISSIONS[location.pathname];
      if (
        requiredPermission &&
        user.company_permissions[`can_${requiredPermission}`] !== 1
      ) {
        navigate("/patrol-reports", { replace: true });
      }
    }

    // multi_company_patrol can only access /active-rounds, /guard-management and /silence-requests
    if (user?.role === "multi_company_patrol") {
      const allowedPatrol = ["/active-rounds", "/guard-management", "/silence-requests"];
      if (!allowedPatrol.includes(location.pathname)) {
        navigate("/active-rounds", { replace: true });
      }
    }

    // Supervisor can only access /patrol-reports and /users
    if (user?.role === "supervisor") {
      const allowed = ["/patrol-reports", "/users"];
      if (!allowed.includes(location.pathname)) {
        navigate("/patrol-reports", { replace: true });
      }
    }
  }, [user?.company_permissions, location.pathname, user?.role, navigate]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Login />;
  }

  // Guard role should not access Admin Dashboard
  if (user.role === "guard") {
    return (
      <Layout
        style={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: "#f0f2f5",
        }}
      >
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            background: "white",
            borderRadius: "8px",
            maxWidth: "500px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <div style={{ fontSize: "64px", marginBottom: "16px" }}>📱</div>
          <h1 style={{ fontSize: "24px", marginBottom: "16px" }}>
            Guard Access Not Available
          </h1>
          <p style={{ fontSize: "16px", color: "#666", marginBottom: "24px" }}>
            Guards should use the <strong>Guard Mobile App</strong> to access
            the system.
          </p>
          <p style={{ fontSize: "14px", color: "#999", marginBottom: "24px" }}>
            Please download and use the Guard Mobile App on your mobile device
            to:
          </p>
          <ul
            style={{ textAlign: "left", marginBottom: "24px", color: "#666" }}
          >
            <li>View your patrol schedule</li>
            <li>Scan checkpoint QR codes</li>
            <li>Submit check-in reports</li>
            <li>View your patrol history</li>
          </ul>
          <button
            onClick={logout}
            style={{
              padding: "10px 24px",
              fontSize: "16px",
              background: "#1890ff",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Logout
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} isMobile={isMobile} />
      <Layout
        style={{
          marginLeft: isMobile ? 0 : 260,
          transition: 'all 0.2s'
        }}
      >
        <Header collapsed={collapsed} setCollapsed={setCollapsed} isMobile={isMobile} />
        <Content
          style={{
            padding: isMobile ? "12px" : "24px",
            minHeight: "280px"
          }}
        >
          <Routes>
            <Route
              path="/"
              element={
                <Navigate
                  to={
                    user?.role === "super_admin"
                      ? "/companies"
                      : user?.role === "multi_company_patrol"
                        ? "/active-rounds"
                        : "/patrol-reports"
                  }
                  replace
                />
              }
            />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/company" element={<Company />} />
            <Route path="/checkpoints" element={<Checkpoints />} />
            <Route path="/patrol-rounds" element={<PatrolRounds />} />
            <Route path="/patrol-reports" element={<PatrolReports />} />
            <Route path="/users" element={<Users />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/companies" element={<AllCompanies />} />
            <Route
              path="/multi-company-admin"
              element={<MultiCompanyAdmin />}
            />
            <Route
              path="/guard-management"
              element={<GuardCompanyManagement />}
            />
            <Route
              path="/active-rounds"
              element={<ActiveRounds />}
            />
            <Route
              path="/silence-requests"
              element={<SilenceRequests />}
            />
            <Route
              path="/guard-performance"
              element={<GuardPerformance />}
            />
            <Route
              path="/executive-dashboard"
              element={<ExecutiveDashboard />}
            />
            <Route
              path="*"
              element={
                <Navigate
                  to={
                    user?.role === "super_admin"
                      ? "/companies"
                      : user?.role === "multi_company_patrol"
                        ? "/active-rounds"
                        : "/patrol-reports"
                  }
                  replace
                />
              }
            />
          </Routes>
        </Content>
      </Layout>
    </Layout>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppLayout />
    </AuthProvider>
  );
}

export default App;
